import { useRef, useState } from "react"
import { CardComponent, cardStyles, cardTitle, CardWrapper, tagsWrapper, tag as tagStyles } from "./styled-components/card"
import { Loader } from "components/loader/loader"
import { ArchiveSvg } from "assets/svg/icon"
import { TApiTag, TApiTaskAssignee, TApiTaskDetails } from "@timenotes/shared/src/services/api-client/types"
import { CSS } from "@dnd-kit/utilities"
import { useSortable } from "@dnd-kit/sortable"
import { useTaskDetailsModal } from "components/timenotes/tasks/task-details-modal/task-details-modal"
import { Avatar, Badge, Button, Tooltip } from "antd"
import { formatTaskDurationWithEstimate } from "@timenotes/shared/src/services/tasks/utils/format-task-duration-with-estimate"
import { isTaskAboveEstimate } from "@timenotes/shared/src/services/tasks/utils/is-task-above-estimate"
import { TeamOutlined } from "@ant-design/icons"
import { UserCircle } from "components/timenotes/tasks/task-details-modal/sidebar/task-assignees-select-popover"

interface TaskCardProps {
  task: TApiTaskDetails
}

interface DraggableTaskCardProps extends TaskCardProps { }

const DraggableTaskCard = (props: DraggableTaskCardProps) => {
  const {
    task
  } = props

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: task.id
  })

  const mergedStyle: React.CSSProperties = {
    //...style,
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: 'grab',
    ...(isDragging ? { opacity: 0.3 } : {}),
  };

  return (
    <CardWrapper
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={{
        marginTop: ".5rem",
        marginBottom: ".5rem",
        transform: CSS.Translate.toString(transform),
        ...mergedStyle,
      }}
    >
      <TaskCard {...props} />
    </CardWrapper>
  )
}

const TaskCard = (props: TaskCardProps) => {
  const {
    task
  } = props

  const propsStyles = {}

  const taskId = task.id
  const text = task.name

  const tags = task.tags || []

  const isTrackerActive = false
  const estimateTime = null 

  const worktime = 50

  const {
    openTaskModal,
  } = useTaskDetailsModal()

  return (
    <CardComponent
      onClick={(e) => {
        e.preventDefault()
        openTaskModal({
          projectId: task.project.id,
          taskId: task.id
        })
      }}
      hoverable={taskId !== "" ? true : false}
      className="card"
      style={{
        ...cardStyles,
        ...propsStyles,
        height: "8.5rem",
      }}
    >
      <Loader
        styles={{
          borderRadius: "0.625rem",
          transform: "translate(-20px, -20px)",
        }}
        visible={taskId === ""}
      />

      <h4 style={cardTitle}>
        {text}
      </h4>

      <section style={tagsWrapper}>
        <TagList tags={tags} />
      </section>

      <section
        style={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <section
          style={{
            fontSize: ".8rem",
            display: "flex",
            alignItems: "center",
          }}
        >
          { task.worktime > 0 && (
            <div
              style={{
                color: isTaskAboveEstimate(task) ? 'red' : undefined
              }}
            >
              { formatTaskDurationWithEstimate(task) }
            </div>
          )}

          {!isTrackerActive && (
            <>
              {estimateTime !== null && <span> of </span>}
              <span>
                {estimateTime?.hour()
                  ? estimateTime?.format("H") + "h"
                  : null}{" "}
              </span>
              <span style={{ fontSize: ".8rem" }}>
                {estimateTime?.minute()
                  ? estimateTime?.format("MM") + "m"
                  : null}{" "}
              </span>
            </>
          )}

          {/*

                <CardTimer
                  visible={timerShowed}
                  activeStartedAt={props.activeStartedAt}
                  activeTimeZone={props.activeTimeZone}
                  start={() =>
                    startTrackerMutation.mutate({ task: { id: props.id } })
                  }
                  stop={() => {
                    stopTrackerMutation
                      .mutateAsync()
                      .then(() =>
                        queryClient.invalidateQueries([
                          "board-tasks",
                          projectId,
                        ])
                      )
                      .catch(() => {})
                  }}
                  started={props.isTrackerActive}
                />

                */}

        </section>
        <section
          style={{
            display: "flex",
            gap: "0.25rem",
            fontSize: ".8rem",
            alignItems: "center",
          }}
        >
          <div>
            {task.state == 'archived' && (
              <Tooltip title="Archived">
                <div
                  style={{
                    borderRadius: '30px',
                    border: '1px solid #ccc',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    color: '#bbb',
                  }}
                >
                  <ArchiveSvg />
                </div>
              </Tooltip>
            )}
          </div>

          <div>
            <AssigneeButton assignees={task.assignees} />
          </div>

        </section>
      </section>
    </CardComponent>
  )
}

const Tag = ({ tag }: { tag: TApiTag }) => (
  <div style={{ ...tagStyles, background: tag.color }}>
    {tag.name.length > 16 ? `${tag.name.slice(0, 12)}...` : tag.name}
  </div>
)

const TagList = ({ tags }: { tags: TApiTag[] }) => {
  const visibleTags = tags.length >= 4 ? tags.slice(0, 3) : tags
  const remainingCount = tags.length - 3

  return (
    <>
      {visibleTags.map((item, index) =>
        index === 2 && tags.length >= 4 ? (
          <Badge key={item.name} count={`+${remainingCount}`} offset={[10, 0]}>
            <Tag tag={item} />
          </Badge>
        ) : (
          <Tag key={item.name} tag={item} />
        )
      )}
    </>
  )
}

const AssigneeButton = ({ assignees }: { assignees: TApiTaskAssignee[] }) => {
  if (!assignees || assignees.length === 0) return null

  const getInitials = (name: string) => {
    const parts = name.split(" ")
    return parts.length > 1 ? `${parts[0][0]}${parts[1][0]}`.toUpperCase() : name[0].toUpperCase()
  }

  const isGroup = assignees.some((assignee) => assignee.type === "MembersGroup")
  const tooltipContent = assignees
    .map((assignee) => `${assignee.type === "UsersAccount" ? assignee.name : `Group: ${assignee.name}`}`)
    .join("\n")

  return (
    <Tooltip title={<span style={{ whiteSpace: "pre-line" }}>{tooltipContent}</span>}>
        {isGroup || assignees.length > 1 ? (
          <div
            style={{
              borderRadius: '30px',
              border: '1px solid #ccc',
              width: '24px',
              height: '24px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              color: '#bbb',
            }}
          >
            <TeamOutlined />
          </div>
        ) : (
          <UserCircle>
            {getInitials(assignees[0].name)}
          </UserCircle>
        )}
    </Tooltip>
  )
}


export default DraggableTaskCard